import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from './axiosInstance';
import { UserContext } from './UserContext';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import './Login.css';

function PasswordSetup() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [verifying, setVerifying] = useState(true);
  const [verifyError, setVerifyError] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const { setUser } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        setVerifying(true);
        setVerifyError('');
        const res = await axios.get('/users/password-setup/verify', { params: { token } });
        setEmail(res.data.email);
        setFirstName(res.data.first_name || '');
      } catch (err) {
        const msg = err.response?.data?.error?.message || err.message || 'Invalid or expired link';
        setVerifyError(msg);
      } finally {
        setVerifying(false);
      }
    })();
  }, [token]);

  const checks = useMemo(() => {
    return {
      length: password.length >= 8,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password)
    };
  }, [password]);

  const allValid = checks.length && checks.upper && checks.lower && checks.number && checks.special && password === confirm && password.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allValid) return;
    try {
      setSubmitting(true);
      setErrorMsg('');
      const res = await axios.post('/users/password-setup', { token, password });
      const { token: authToken, user } = res.data;
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify({ ...user, authenticated: true }));
      if (user && user.email) {
        localStorage.setItem('userEmail', user.email);
      }
      setUser({ ...user, authenticated: true });
      window.dispatchEvent(new Event('pip-login-success'));
      navigate('/Pips/pips');
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.message || 'Failed to set password';
      setErrorMsg('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const Item = ({ ok, text }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: ok ? '#16a34a' : '#dc2626' }}>
      {ok ? <FaCheckCircle /> : <FaTimesCircle />}
      <span>{text}</span>
    </div>
  );

  if (verifying) {
    return (
      <div className="login-container">
        <div className="login-card"><p>Verifying link...</p></div>
      </div>
    );
  }

  if (verifyError) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="error-message">{verifyError}</div>
          <button className="login-button" onClick={() => navigate('/login')}>Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1 className="login-title">Set Your Password</h1>
          <p className="login-subtitle">{firstName ? `Hi ${firstName},` : ''} create your password to continue.</p>
          <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>{email}</p>
        </div>

        {errorMsg && (
          <div className="error-message">{errorMsg}</div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              className="form-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>

          <div style={{ display: 'grid', gap: 6, margin: '10px 0 16px 0' }}>
            <Item ok={checks.length} text="At least 8 characters" />
            <Item ok={checks.upper} text="At least 1 uppercase letter" />
            <Item ok={checks.lower} text="At least 1 lowercase letter" />
            <Item ok={checks.number} text="At least 1 number" />
            <Item ok={checks.special} text="At least 1 special character" />
            <Item ok={password === confirm && confirm.length > 0} text="Passwords match" />
          </div>

          <div className="form-actions">
            <button type="submit" className="login-button" disabled={!allValid || submitting}>
              {submitting ? 'Setting password...' : 'Set Password & Login'}
            </button>
          </div>
        </form>

        <div className="login-footer">
          <p className="footer-text">Having trouble? Contact your administrator.</p>
        </div>
      </div>
    </div>
  );
}

export default PasswordSetup;
